import React, { useRef, useEffect, useState } from 'react';
import { formatPhotoclearCreditLabel, quotePhotoclearOperation } from '@rehub/pricing';
import { Eraser, Brush, Undo, Redo, Trash2, ArrowRight, Loader2, Sparkles, Flower2, BedDouble, Download, ChevronRight, Crosshair, Wand2, MapPin, LayoutDashboard, Car, Sun, TreeDeciduous, ChevronUp, ChevronDown, X, Cloud, Sunset, Home, Eye, EyeOff, Maximize2, ImagePlus, Sofa, Lightbulb, SunDim, RectangleEllipsis, Scissors } from 'lucide-react';
import Viewer360 from './Viewer360';
import BotanicalIcon from './BotanicalIcon';
import DeclutterIcon from './DeclutterIcon';
import { autoStraightenImage, drawAlignmentGrid, ensureOpenCVLoaded, applyPerspectiveCorrection, smartCrop, matToDataURL } from '../services/verticalAlignment';
import { 
  DEFAULT_BRUSH_SIZE, 
  MIN_BRUSH_SIZE, 
  MAX_BRUSH_SIZE, 
  MASK_COLOR, 
  COMPOSITE_MASK_COLOR,
  DEFAULT_DECLUTTER_PROMPT,
  AUTO_DECLUTTER_PROMPT,
  DECLUTTER_INTENSITIES,
  GARDEN_DECLUTTER_PROMPT,
  CUT_GRASS_PROMPT,
  DRIVEWAY_DECLUTTER_PROMPT,
  BINS_ONLY_PROMPT,
  CARS_ONLY_PROMPT,
  BED_DECLUTTER_PROMPT,
  SOFA_STRAIGHTEN_PROMPT,
  BRIGHTEN_ENHANCE_INTENSITIES,
  TURN_ON_LIGHTS_PROMPT,
  BLUR_PERSONAL_PHOTOS_PROMPT,
  BLUR_LICENSE_PLATES_PROMPT,
  REMOVE_SHADOWS_PROMPT,
  BLUE_SKY_PROMPT_TEMPLATE,
  BLUE_SKY_TYPES,
  SUNSET_SKY_PROMPT,
  WINDOW_SKY_PROMPT,
  SUNSET_TYPES,
  ADD_FLOWERS_PROMPT,
  MANUAL_FLOWERS_PROMPT,
  PIN_FLOWERS_PROMPT,
  FLOWER_TYPES,
  PLANT_TYPES,
  ADD_PLANTS_PROMPT,
  MANUAL_PLANTS_PROMPT,
  PIN_PLANTS_PROMPT,
  MAX_PROCESSING_DIMENSION,
  GEMINI_INPUT_JPEG_QUALITY,
  hasBlockedStagingIntent
} from '../constants';

/** PNG when a red mask must stay crisp; JPEG otherwise so a 4K exterior fits the image worker. */
function encodeForGemini(canvas: HTMLCanvasElement, keepMask: boolean): string {
  if (keepMask) return canvas.toDataURL('image/png');
  return canvas.toDataURL('image/jpeg', GEMINI_INPUT_JPEG_QUALITY);
}

function skyFanVars(
  index: number,
  count: number,
  { step = 50, arch = 36, maxAngle = 16, delayStep = 40 }: { step?: number; arch?: number; maxAngle?: number; delayStep?: number } = {},
) {
  const mid = (count - 1) / 2;
  const t = mid === 0 ? 0 : (index - mid) / mid;
  const angleDeg = t * maxAngle;
  return {
    ['--fan-x' as string]: `${((index - mid) * step).toFixed(1)}px`,
    ['--fan-y' as string]: `${(arch * (t * t - 1)).toFixed(1)}px`,
    ['--fan-rot' as string]: `${angleDeg.toFixed(2)}deg`,
    ['--fan-rot-neg' as string]: `${(-angleDeg).toFixed(2)}deg`,
    ['--fan-delay' as string]: `${index * delayStep}ms`,
    zIndex: Math.round(10 - Math.abs(index - mid)),
  };
}

function useDelayedOpen(open: boolean, delay = 80) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setReady(false);
      return;
    }
    const id = window.setTimeout(() => setReady(true), delay);
    return () => window.clearTimeout(id);
  }, [open, delay]);
  return ready;
}

interface EditorProps {
  imageUrl: string;
  onProcess: (
    imageData: string,
    prompt: string,
    aspectRatio: string,
    restoreRect: {x: number, y: number, width: number, height: number} | null,
    is360?: boolean,
    source?: 'preset' | 'custom',
    operation?: string
  ) => void;
  isProcessing: boolean;
  onDownload: () => void;
  onOpenDashboard: () => void;
  actionCredits?: Record<string, number>;
  onReserveAction?: (operation: string) => Promise<string | null>;
  onFinishAction?: (refId: string, success: boolean) => Promise<void>;
  /** Opens the declutter intensity picker without starting a paid edit. */
  pendingDeclutterId?: string | null;
}

interface HistoryStep {
  imageData: ImageData;
  hasMask: boolean;
}

const Editor: React.FC<EditorProps> = ({ imageUrl, onProcess, isProcessing, onDownload, onOpenDashboard, actionCredits, onReserveAction, onFinishAction, pendingDeclutterId }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null); // Layer for drawing mask
  const imageRef = useRef<HTMLImageElement>(null); // Hidden image element
  
  // State
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(DEFAULT_BRUSH_SIZE);
  const [mode, setMode] = useState<'brush' | 'eraser'>('brush');
  const [hasMask, setHasMask] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [selectedFlowerId, setSelectedFlowerId] = useState(FLOWER_TYPES[0].id);
  const [selectedPlantId, setSelectedPlantId] = useState(PLANT_TYPES[0].id);
  const [declutterFanOpen, setDeclutterFanOpen] = useState(false);
  const [brightenFanOpen, setBrightenFanOpen] = useState(false);
  useEffect(() => {
    if (pendingDeclutterId) setDeclutterFanOpen(true);
  }, [pendingDeclutterId, imageUrl]);
  const [blueSkyFanOpen, setBlueSkyFanOpen] = useState(false);
  const [sunsetFanOpen, setSunsetFanOpen] = useState(false);
  const [flowerPickerOpen, setFlowerPickerOpen] = useState(false);
  const [plantPickerOpen, setPlantPickerOpen] = useState(false);
  
  // Mobile bottom sheet state
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<'quick' | 'custom'>('quick');
  
  // Pin Location State
  const [isPlacingPin, setIsPlacingPin] = useState(false);
  const [pinLocation, setPinLocation] = useState<{x: number, y: number} | null>(null);
  
  // 360 Image Detection
  const [is360Image, setIs360Image] = useState(false);
  const [show360Viewer, setShow360Viewer] = useState(false);
  // Opt-in for experimental AI declutter on 360° panoramas (geometry tools stay locked)
  const [enable360Editing, setEnable360Editing] = useState(false);

  const SEAM_PRESERVE_NOTE =
    "\n\nIMPORTANT: This is a 360° equirectangular panorama (2:1). Preserve left–right edge continuity so the image wraps seamlessly, and do not distort the poles or overall projection geometry.";

  // History for undo/redo (storing ImageData and mask state)
  const [history, setHistory] = useState<HistoryStep[]>([]);
  const [redoHistory, setRedoHistory] = useState<HistoryStep[]>([]);
  
  // Vertical Alignment State
  const [isAligning, setIsAligning] = useState(false);
  const [showAlignmentGrid, setShowAlignmentGrid] = useState(false);
  const [alignmentError, setAlignmentError] = useState<string | null>(null);
  const [displayImageUrl, setDisplayImageUrl] = useState<string>(imageUrl);
  const [isDrawingVerticalLine, setIsDrawingVerticalLine] = useState(false);
  const [verticalLineStart, setVerticalLineStart] = useState<{x: number, y: number} | null>(null);
  const [verticalLineEnd, setVerticalLineEnd] = useState<{x: number, y: number} | null>(null);
  const gridCanvasRef = useRef<HTMLCanvasElement>(null);

  // Update display URL when imageUrl prop changes
  useEffect(() => {
    setDisplayImageUrl(imageUrl);
  }, [imageUrl]);

  useEffect(() => {
    if (isProcessing) {
      setDeclutterFanOpen(false);
      setBrightenFanOpen(false);
      setBlueSkyFanOpen(false);
      setSunsetFanOpen(false);
      setFlowerPickerOpen(false);
      setPlantPickerOpen(false);
    }
  }, [isProcessing]);

  useEffect(() => {
    if (!declutterFanOpen && !brightenFanOpen && !blueSkyFanOpen && !sunsetFanOpen && !flowerPickerOpen && !plantPickerOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && !target.closest('[data-sky-fan]') && !target.closest('[data-botanical-picker]')) {
        setDeclutterFanOpen(false);
        setBrightenFanOpen(false);
        setBlueSkyFanOpen(false);
        setSunsetFanOpen(false);
        setFlowerPickerOpen(false);
        setPlantPickerOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDeclutterFanOpen(false);
        setBrightenFanOpen(false);
        setBlueSkyFanOpen(false);
        setSunsetFanOpen(false);
        setFlowerPickerOpen(false);
        setPlantPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [declutterFanOpen, brightenFanOpen, blueSkyFanOpen, sunsetFanOpen, flowerPickerOpen, plantPickerOpen]);

  // Initialize Canvas and handle Resize
  useEffect(() => {
    const img = new Image();
    img.src = displayImageUrl;
    
    const calculateSize = () => {
        if (containerRef.current && img.width > 0) {
            const containerW = containerRef.current.clientWidth;
            const containerH = containerRef.current.clientHeight;
            const imgAspect = img.width / img.height;
            const containerAspect = containerW / containerH;
    
            let renderW, renderH;
    
            if (imgAspect > containerAspect) {
              renderW = containerW;
              renderH = containerW / imgAspect;
            } else {
              renderH = containerH;
              renderW = containerH * imgAspect;
            }
    
            setCanvasSize({ width: renderW, height: renderH });
        }
    };

    img.onload = () => {
        imageRef.current = img;
        calculateSize();
        
        // Check if image is 360 equirectangular
        const ratio = img.width / img.height;
        const is360 = Math.abs(ratio - 2.0) < 0.1;
        setIs360Image(is360);
        
        // Reset state only on initial load, not resize
        if (history.length === 0) {
             setEnable360Editing(false);
             setHistory([]);
             setRedoHistory([]);
             setHasMask(false);
             setCustomPrompt("");
             setIsDrawing(false);
             setPinLocation(null);
             setIsPlacingPin(false);
             const ctx = canvasRef.current?.getContext('2d');
             if (ctx && canvasRef.current) {
                  ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
             }
        }
    };

    window.addEventListener('resize', calculateSize);
    return () => window.removeEventListener('resize', calculateSize);
  }, [displayImageUrl]); // Dependency on displayImageUrl for internal updates

  // Handle Canvas Resize (Sync Buffer).
  // Also re-run when 360 editing is enabled so a remounted mask canvas
  // gets the correct pixel buffer (CSS size alone is not enough to draw).
  useEffect(() => {
    if (canvasRef.current && canvasSize.width > 0) {
      const canvas = canvasRef.current;
      const tempCtx = document.createElement('canvas').getContext('2d');
      let savedData = null;
      if (tempCtx && canvas.width > 0) {
          tempCtx.canvas.width = canvas.width;
          tempCtx.canvas.height = canvas.height;
          tempCtx.drawImage(canvas, 0, 0);
          savedData = tempCtx.canvas;
      }

      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      
      const ctx = canvas.getContext('2d');
      if (ctx && savedData) {
         ctx.drawImage(savedData, 0, 0, canvas.width, canvas.height);
      }
    }
    
    // Also resize grid canvas
    if (gridCanvasRef.current && canvasSize.width > 0) {
      gridCanvasRef.current.width = canvasSize.width;
      gridCanvasRef.current.height = canvasSize.height;
    }
  }, [canvasSize, enable360Editing]);
  
  // Draw alignment grid when enabled
  useEffect(() => {
    if (showAlignmentGrid && gridCanvasRef.current) {
      const ctx = gridCanvasRef.current.getContext('2d');
      if (ctx) {
        // Clear previous grid
        ctx.clearRect(0, 0, gridCanvasRef.current.width, gridCanvasRef.current.height);
        // Draw new grid
        drawAlignmentGrid(gridCanvasRef.current, ctx, 'rgba(0, 255, 255, 0.5)', 2);
      }
    } else if (gridCanvasRef.current) {
      const ctx = gridCanvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, gridCanvasRef.current.width, gridCanvasRef.current.height);
      }
    }
  }, [showAlignmentGrid, canvasSize]);

  // Drawing Logic
  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const handleCanvasMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (isProcessing) return;

    // Handle Vertical Line Drawing
    if (isDrawingVerticalLine) {
        const { x, y } = getCoordinates(e);
        if (!verticalLineStart) {
            // First click - set start point
            setVerticalLineStart({ x, y });
        } else {
            // Second click - set end point and trigger alignment
            setVerticalLineEnd({ x, y });
            handleManualAlignment({ x, y });
        }
        return;
    }

    // Handle Pin Placement
    if (isPlacingPin) {
        const { x, y } = getCoordinates(e);
        // Store normalized coordinates
        if (canvasSize.width > 0 && canvasSize.height > 0) {
            setPinLocation({
                x: x / canvasSize.width,
                y: y / canvasSize.height
            });
            setIsPlacingPin(false);
        }
        return;
    }

    // Normal Drawing
    startDrawing(e);
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDrawing(true);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !canvasRef.current) return;

    // If we start drawing, clear pin to avoid confusion
    if (pinLocation) setPinLocation(null);

    // Save history before new stroke
    const currentStep: HistoryStep = {
        imageData: ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height),
        hasMask: hasMask
    };
    setHistory(prev => [...prev.slice(-20), currentStep]); 
    setRedoHistory([]); 

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = brushSize;
    ctx.strokeStyle = mode === 'brush' ? MASK_COLOR : 'rgba(0,0,0,1)';
    
    if (mode === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
    } else {
      ctx.globalCompositeOperation = 'source-over';
    }
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (isPlacingPin) return; // Don't draw while placing pin
    if (isDrawingVerticalLine) return; // Don't draw while drawing vertical line
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    
    if (!hasMask) setHasMask(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const ctx = canvasRef.current?.getContext('2d');
      ctx?.closePath();
      setIsDrawing(false);
      if (ctx) ctx.globalCompositeOperation = 'source-over';
    }
  };

  const handleUndo = () => {
    if (history.length === 0 || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    
    const currentStep: HistoryStep = {
        imageData: ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height),
        hasMask: hasMask
    };
    
    const previousStep = history[history.length - 1];
    
    ctx.putImageData(previousStep.imageData, 0, 0);
    setHasMask(previousStep.hasMask);
    
    setRedoHistory(prev => [...prev, currentStep]);
    setHistory(prev => prev.slice(0, -1));
  };

  const handleRedo = () => {
    if (redoHistory.length === 0 || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const currentStep: HistoryStep = {
        imageData: ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height),
        hasMask: hasMask
    };

    const nextStep = redoHistory[redoHistory.length - 1];

    ctx.putImageData(nextStep.imageData, 0, 0);
    setHasMask(nextStep.hasMask);

    setHistory(prev => [...prev, currentStep]);
    setRedoHistory(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    
    const currentStep: HistoryStep = {
        imageData: ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height),
        hasMask: hasMask
    };
    setHistory(prev => [...prev.slice(-20), currentStep]);
    setRedoHistory([]); 
    
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasMask(false);
    setPinLocation(null);
    setVerticalLineStart(null);
    setVerticalLineEnd(null);
  };

  const is360EquirectangularImage = (width: number, height: number): boolean => {
    const ratio = width / height;
    // 360 equirectangular images are 2:1 aspect ratio (tolerance of ±5%)
    return Math.abs(ratio - 2.0) < 0.1;
  };

  const getBestAspectRatioObj = (width: number, height: number) => {
    const ratio = width / height;
    const supported = [
      { id: "1:1", val: 1 },
      { id: "3:4", val: 3/4 },
      { id: "4:3", val: 4/3 },
      { id: "9:16", val: 9/16 },
      { id: "16:9", val: 16/9 },
      { id: "21:9", val: 21/9 }
    ];
    
    return supported.reduce((prev, curr) => {
      return (Math.abs(curr.val - ratio) < Math.abs(prev.val - ratio) ? curr : prev);
    });
  };

  // Modified to support Pin Location and 360 images
  const prepareDataForProcessing = (includeMask: boolean, pinPos: {x: number, y: number} | null = null) => {
    if (!imageRef.current) return null;
    // Mask / pin paths need the drawing canvas; full-image AI does not
    if ((includeMask || pinPos) && !canvasRef.current) return null;

    const originalW = imageRef.current.width;
    const originalH = imageRef.current.height;
    
    // Check if this is a 360 equirectangular image
    const is360 = is360EquirectangularImage(originalW, originalH);
    
    // For 360 images, use 21:9 ratio (Gemini limitation workaround)
    if (is360) {
      // Use 21:9 (2.33:1) - closest supported ratio to 2:1
      const targetRatio = 21/9;
      
      let finalW = originalW;
      let finalH = originalW / targetRatio; // Add vertical padding
      let finalScale = 1;
      
      // Scale down if needed
      if (finalW > MAX_PROCESSING_DIMENSION || finalH > MAX_PROCESSING_DIMENSION) {
        if (finalW > finalH) {
          finalScale = MAX_PROCESSING_DIMENSION / finalW;
          finalW = MAX_PROCESSING_DIMENSION;
          finalH = finalW / targetRatio;
        } else {
          finalScale = MAX_PROCESSING_DIMENSION / finalH;
          finalH = MAX_PROCESSING_DIMENSION;
          finalW = finalH * targetRatio;
        }
      }
      
      const scaledOriginalW = originalW * finalScale;
      const scaledOriginalH = originalH * finalScale;
      const offsetY = (finalH - scaledOriginalH) / 2;
      
      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = finalW;
      outputCanvas.height = finalH;
      const ctx = outputCanvas.getContext('2d');
      if (!ctx) return null;
      
      // PIXEL-PERFECT: Disable smoothing when not scaling, enable high-quality when scaling
      const isScaling = finalScale !== 1;
      ctx.imageSmoothingEnabled = isScaling;
      if (isScaling) ctx.imageSmoothingQuality = 'high';
      
      // Black padding
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, finalW, finalH);
      
      // Draw image centered vertically
      ctx.drawImage(
        imageRef.current,
        0, 0, originalW, originalH,
        0, offsetY, scaledOriginalW, scaledOriginalH
      );
      
      // Add mask if needed
      if (includeMask || pinPos) {
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = finalW;
        maskCanvas.height = finalH;
        const mCtx = maskCanvas.getContext('2d');
        
        if (mCtx) {
          if (pinPos) {
            // Draw pin location (adjust for offset)
            const pinX = pinPos.x * scaledOriginalW;
            const pinY = offsetY + (pinPos.y * scaledOriginalH);
            
            mCtx.fillStyle = COMPOSITE_MASK_COLOR;
            mCtx.beginPath();
            mCtx.arc(pinX, pinY, 20 * finalScale, 0, Math.PI * 2);
            mCtx.fill();
          } else if (canvasRef.current) {
            // Regular mask from canvas (adjust for offset)
            const maskSource = canvasRef.current;
            mCtx.drawImage(
              maskSource,
              0, 0, maskSource.width, maskSource.height,
              0, offsetY, scaledOriginalW, scaledOriginalH
            );
            
            mCtx.globalCompositeOperation = 'source-in';
            mCtx.fillStyle = COMPOSITE_MASK_COLOR;
            mCtx.fillRect(0, 0, finalW, finalH);
          }
          
          ctx.drawImage(maskCanvas, 0, 0);
        }
      }
      
      // Store crop info to restore 2:1 from 21:9
      const restoreRect = {
        x: 0,
        y: offsetY / finalH,
        width: 1,
        height: scaledOriginalH / finalH
      };
      
      return {
        dataUrl: encodeForGemini(outputCanvas, includeMask || !!pinPos),
        aspectRatioId: "21:9", // Use supported Gemini ratio
        restoreRect, // Crop back to 2:1
        is360: true
      };
    }

    // Non-360 images: apply standard padding logic
    const targetAspectObj = getBestAspectRatioObj(originalW, originalH);
    const targetRatio = targetAspectObj.val;

    let paddedW, paddedH, offsetX, offsetY;
    const originalRatio = originalW / originalH;

    if (originalRatio > targetRatio) {
      paddedW = originalW;
      paddedH = originalW / targetRatio;
      offsetX = 0;
      offsetY = (paddedH - originalH) / 2;
    } else {
      paddedH = originalH;
      paddedW = originalH * targetRatio;
      offsetX = (paddedW - originalW) / 2;
      offsetY = 0;
    }

    let finalW = paddedW;
    let finalH = paddedH;
    let finalScale = 1;

    if (finalW > MAX_PROCESSING_DIMENSION || finalH > MAX_PROCESSING_DIMENSION) {
        if (finalW > finalH) {
            finalScale = MAX_PROCESSING_DIMENSION / finalW;
            finalW = MAX_PROCESSING_DIMENSION;
            finalH = finalW / targetRatio;
        } else {
            finalScale = MAX_PROCESSING_DIMENSION / finalH;
            finalH = MAX_PROCESSING_DIMENSION;
            finalW = finalH * targetRatio;
        }
    }

    const scaledOffsetX = offsetX * finalScale;
    const scaledOffsetY = offsetY * finalScale;
    const scaledOriginalW = originalW * finalScale;
    const scaledOriginalH = originalH * finalScale;

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = finalW;
    outputCanvas.height = finalH;
    const ctx = outputCanvas.getContext('2d');
    if (!ctx) return null;

    // PIXEL-PERFECT: Disable smoothing when not scaling, enable high-quality when scaling
    const isScaling = finalScale !== 1;
    ctx.imageSmoothingEnabled = isScaling;
    if (isScaling) ctx.imageSmoothingQuality = 'high';

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, finalW, finalH);

    ctx.drawImage(
        imageRef.current, 
        0, 0, originalW, originalH,
        scaledOffsetX, scaledOffsetY, scaledOriginalW, scaledOriginalH
    );

    if (includeMask || pinPos) {
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = finalW;
        maskCanvas.height = finalH;
        const mCtx = maskCanvas.getContext('2d');
        
        if (mCtx) {
            if (pinPos) {
                // If using pin, draw a circle at the pin location
                // Calculate position on the FINAL canvas
                const pinX = scaledOffsetX + (pinPos.x * scaledOriginalW);
                const pinY = scaledOffsetY + (pinPos.y * scaledOriginalH);
                
                mCtx.fillStyle = COMPOSITE_MASK_COLOR;
                mCtx.beginPath();
                // Draw a small dot (e.g. 15px radius adjusted for scale)
                mCtx.arc(pinX, pinY, 20 * finalScale, 0, Math.PI * 2);
                mCtx.fill();
            } else if (canvasRef.current) {
                // Regular mask from canvas
                const maskSource = canvasRef.current;
                mCtx.drawImage(
                    maskSource,
                    0, 0, maskSource.width, maskSource.height,
                    scaledOffsetX, scaledOffsetY, scaledOriginalW, scaledOriginalH
                );
                
                mCtx.globalCompositeOperation = 'source-in';
                mCtx.fillStyle = COMPOSITE_MASK_COLOR;
                mCtx.fillRect(0, 0, finalW, finalH);
            }

            ctx.drawImage(maskCanvas, 0, 0);
        }
    }

    const restoreRect = {
        x: scaledOffsetX / finalW,
        y: scaledOffsetY / finalH,
        width: scaledOriginalW / finalW,
        height: scaledOriginalH / finalH
    };

    return {
        dataUrl: encodeForGemini(outputCanvas, includeMask || !!pinPos),
        aspectRatioId: targetAspectObj.id,
        restoreRect,
        is360: false
    };
  };

  const require360OptIn = (): boolean => {
    if (!is360Image || enable360Editing) return true;
    const ok = window.confirm(
      "Experimental 360° declutter\n\n" +
      "AI edits on equirectangular panoramas can break the wrap seam or look wrong in a 360° viewer.\n\n" +
      "Brush masking is available for targeted edits. Straighten stays disabled. Preview the result in the 360° viewer before downloading.\n\n" +
      "Continue and enable experimental declutter?"
    );
    if (ok) setEnable360Editing(true);
    return ok;
  };

  const canUseBrush = !is360Image || enable360Editing;

  const handleAutoProcess = (prompt: string, operation: string) => {
    if (isProcessing) return;
    if (!require360OptIn()) return;
    let finalPrompt = prompt;
    if (hasMask) {
      finalPrompt += "\n\nIMPORTANT: The areas highlighted with a red overlay in this image are editing markers indicating WHERE to focus these changes. Apply the requested modifications primarily within the red-highlighted regions. The red marking is an editing indicator ONLY — do NOT reproduce any red color, red tint, or color cast in the output image. The output must have natural, unaltered colors.";
    }
    if (is360Image) {
      finalPrompt += SEAM_PRESERVE_NOTE;
    }
    const result = prepareDataForProcessing(hasMask);
    if (result) {
        onProcess(result.dataUrl, finalPrompt, result.aspectRatioId, result.restoreRect, result.is360, undefined, operation);
    }
  };

  const handleManualProcess = () => {
    if (isProcessing) return;
    if (!require360OptIn()) return;

    let promptToUse = customPrompt.trim();
    const isBlockedCustomPrompt = hasBlockedStagingIntent(promptToUse);
    
    // Default removal behavior if mask exists but no prompt
    if (hasMask && !promptToUse) {
        promptToUse = DEFAULT_DECLUTTER_PROMPT;
    }

    // Require prompt if no mask (Global Edit)
    if (!hasMask && !promptToUse) {
        return;
    }

    if (isBlockedCustomPrompt) {
      return;
    }

    // Append mask context for custom prompts that don't already reference the red mask
    if (hasMask && promptToUse !== DEFAULT_DECLUTTER_PROMPT && !promptToUse.toLowerCase().includes('red mask')) {
        promptToUse += "\n\nIMPORTANT: The areas highlighted with a red overlay in this image are editing markers indicating WHERE to apply these changes. The red marking is an editing indicator ONLY — do NOT reproduce any red color, red tint, or color cast in the output image.";
    }

    if (is360Image) {
      promptToUse += SEAM_PRESERVE_NOTE;
    }

    const result = prepareDataForProcessing(hasMask);
    if (result) {
        onProcess(result.dataUrl, promptToUse, result.aspectRatioId, result.restoreRect, result.is360, 'custom', 'photoclear_custom');
    }
  };

  const handleAddFlowers = () => {
    if (isProcessing) return;
    
    const flower = FLOWER_TYPES.find(f => f.id === selectedFlowerId) || FLOWER_TYPES[0];
    const description = flower.description;

    if (pinLocation) {
        // Use Pin Logic
        const result = prepareDataForProcessing(false, pinLocation);
        if (result) {
            const prompt = PIN_FLOWERS_PROMPT.replace('{{FLOWERS}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360, undefined, 'photoclear_add_flowers');
        }
    } else if (hasMask) {
        // Use Manual Mask Logic
        const result = prepareDataForProcessing(true);
        if (result) {
            const prompt = MANUAL_FLOWERS_PROMPT.replace('{{FLOWERS}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360, undefined, 'photoclear_add_flowers');
        }
    } else {
        // Use Auto Logic
        const prompt = ADD_FLOWERS_PROMPT.replace('{{FLOWERS}}', description);
        handleAutoProcess(prompt, 'photoclear_add_flowers');
    }
  };

  const handleAddPlants = () => {
    if (isProcessing) return;
    
    const plant = PLANT_TYPES.find(p => p.id === selectedPlantId) || PLANT_TYPES[0];
    const description = plant.description;

    if (pinLocation) {
        // Use Pin Logic
        const result = prepareDataForProcessing(false, pinLocation);
        if (result) {
            const prompt = PIN_PLANTS_PROMPT.replace('{{PLANT}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360, undefined, 'photoclear_add_plants');
        }
    } else if (hasMask) {
        // Use Manual Mask Logic
        const result = prepareDataForProcessing(true);
        if (result) {
            const prompt = MANUAL_PLANTS_PROMPT.replace('{{PLANT}}', description);
            onProcess(result.dataUrl, prompt, result.aspectRatioId, result.restoreRect, result.is360, undefined, 'photoclear_add_plants');
        }
    } else {
        // Use Auto Logic
        const prompt = ADD_PLANTS_PROMPT.replace('{{PLANT}}', description);
        handleAutoProcess(prompt, 'photoclear_add_plants');
    }
  };

  const handleSunsetSky = (sunsetId: string) => {
    if (isProcessing) return;
    const sunset = SUNSET_TYPES.find(s => s.id === sunsetId) || SUNSET_TYPES[0];
    const prompt = SUNSET_SKY_PROMPT.replace('{{SUNSET_TYPE}}', sunset.description);
    setSunsetFanOpen(false);
    handleAutoProcess(prompt, 'photoclear_sunset_sky');
  };

  const handleBlueSky = (skyId: string) => {
    if (isProcessing) return;
    const sky = BLUE_SKY_TYPES.find((type) => type.id === skyId) || BLUE_SKY_TYPES[1];
    const prompt = BLUE_SKY_PROMPT_TEMPLATE.replace('{{SKY_TYPE}}', sky.description);
    setBlueSkyFanOpen(false);
    handleAutoProcess(prompt, 'photoclear_blue_sky');
  };

  const handleDeclutterIntensity = (intensityId: string) => {
    if (isProcessing) return;
    const intensity = DECLUTTER_INTENSITIES.find((item) => item.id === intensityId) || DECLUTTER_INTENSITIES[1];
    setDeclutterFanOpen(false);
    handleAutoProcess(intensity.prompt, `photoclear_declutter_${intensity.id}`);
  };

  const handleBrightenIntensity = (intensityId: string) => {
    if (isProcessing) return;
    const intensity = BRIGHTEN_ENHANCE_INTENSITIES.find((item) => item.id === intensityId) || BRIGHTEN_ENHANCE_INTENSITIES[1];
    setBrightenFanOpen(false);
    handleAutoProcess(intensity.prompt, 'photoclear_brighten_enhance');
  };

  // Handle Manual Alignment (user draws vertical line)
  const handleManualAlignment = async (endPoint: {x: number, y: number}) => {
    if (!verticalLineStart || !imageRef.current) return;
    
    console.log('🔧 Starting manual alignment...');
    setIsAligning(true);
    setShowAlignmentGrid(true);
    setAlignmentError(null);
    let reservationRef: string | null = null;
    let billSettled = false;
    const finishStraightenBill = async (success: boolean) => {
      if (!reservationRef || billSettled || !onFinishAction) return;
      billSettled = true;
      await onFinishAction(reservationRef, success);
    };
    
    try {
      // Calculate angle from user-drawn line
      const dx = endPoint.x - verticalLineStart.x;
      const dy = endPoint.y - verticalLineStart.y;
      const angleRad = Math.atan2(dy, dx);
      const angleDeg = (angleRad * 180) / Math.PI;
      
      // Convert to deviation from vertical (90 degrees)
      let correctionAngle = angleDeg - 90;
      
      // Normalize to -45 to 45 range
      if (correctionAngle > 45) correctionAngle -= 90;
      if (correctionAngle < -45) correctionAngle += 90;
      
      console.log('📐 User line angle:', angleDeg.toFixed(2), '°');
      console.log('🎯 Correction angle:', correctionAngle.toFixed(2), '°');
      
      if (Math.abs(correctionAngle) < 0.5) {
        console.log('✓ Line is already vertical');
        setAlignmentError('Line is already vertical (< 0.5° deviation)');
        setVerticalLineStart(null);
        setVerticalLineEnd(null);
        setIsDrawingVerticalLine(false);
        setTimeout(() => {
          setShowAlignmentGrid(false);
          setAlignmentError(null);
        }, 3000);
        return;
      }
      
      // Get current image
      const canvas = document.createElement('canvas');
      const img = imageRef.current;
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) throw new Error('Failed to get canvas context');
      
      ctx.drawImage(img, 0, 0);
      const imageDataUrl = canvas.toDataURL('image/png');

      if (onReserveAction) {
        reservationRef = await onReserveAction('photoclear_straighten_image');
        if (!reservationRef) return;
      }
      
      // Apply rotation using our existing service
      await ensureOpenCVLoaded();
      const srcCanvas = document.createElement('canvas');
      const srcImg = new Image();
      
      await new Promise<void>((resolve, reject) => {
        srcImg.onload = () => resolve();
        srcImg.onerror = reject;
        srcImg.src = imageDataUrl;
      });
      
      srcCanvas.width = srcImg.width;
      srcCanvas.height = srcImg.height;
      const srcCtx = srcCanvas.getContext('2d');
      if (!srcCtx) throw new Error('Failed to get canvas context');
      
      srcCtx.drawImage(srcImg, 0, 0);
      const src = (window as any).cv.imread(srcCanvas);
      const originalAspectRatio = src.cols / src.rows;
      
      try {
        // Apply perspective correction using OpenCV
        const corrected = applyPerspectiveCorrection(src, correctionAngle);
        const cropped = smartCrop(corrected, originalAspectRatio);
        const resultDataUrl = matToDataURL(cropped);
        
        src.delete();
        corrected.delete();
        cropped.delete();
        
        // Convert to blob and update display
        const response = await fetch(resultDataUrl);
        const blob = await response.blob();
        const file = new File([blob], 'aligned-image.png', { type: 'image/png' });
        const url = URL.createObjectURL(file);
        
        console.log('✨ Manual alignment complete!');
        setDisplayImageUrl(url);
        await finishStraightenBill(true);
        
        // Reset line drawing state
        setVerticalLineStart(null);
        setVerticalLineEnd(null);
        setIsDrawingVerticalLine(false);
        
        // Keep grid visible for 2 seconds
        setTimeout(() => {
          setShowAlignmentGrid(false);
        }, 2000);
        
      } catch (error) {
        src.delete();
        throw error;
      }
      
    } catch (error: any) {
      console.error('❌ Manual alignment error:', error);
      await finishStraightenBill(false);
      setAlignmentError('Failed to straighten image. Please try again.');
      setVerticalLineStart(null);
      setVerticalLineEnd(null);
      setIsDrawingVerticalLine(false);
      setTimeout(() => {
        setShowAlignmentGrid(false);
        setAlignmentError(null);
      }, 3000);
    } finally {
      setIsAligning(false);
    }
  };

  // Handle Auto-Straighten (legacy automatic detection)
  const handleAutoStraighten = async () => {
    if (isProcessing || isAligning || !imageRef.current) return;
    
    console.log('🔧 Starting auto-straighten...');
    setIsAligning(true);
    setShowAlignmentGrid(true);
    setAlignmentError(null);
    
    try {
      // Get current image data URL
      const canvas = document.createElement('canvas');
      const img = imageRef.current;
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) throw new Error('Failed to get canvas context');
      
      ctx.drawImage(img, 0, 0);
      const imageDataUrl = canvas.toDataURL('image/png');
      console.log('📸 Image captured, dimensions:', img.width, 'x', img.height);
      
      // Apply vertical alignment
      console.log('🔍 Calling autoStraightenImage...');
      const result = await autoStraightenImage(imageDataUrl);
      console.log('✅ Result:', result);
      
      if (!result.success) {
        console.warn('⚠️ Alignment unsuccessful:', result.error);
        setAlignmentError(result.error || 'Alignment failed');
        setTimeout(() => {
          setShowAlignmentGrid(false);
          setAlignmentError(null);
        }, 3000);
        return;
      }
      
      console.log('🎯 Angle corrected by:', result.angle?.toFixed(2), 'degrees');
      
      // Convert result to File and update image
      const response = await fetch(result.correctedImageData);
      const blob = await response.blob();
      const file = new File([blob], 'aligned-image.png', { type: 'image/png' });
      
      // Update the image URL
      const url = URL.createObjectURL(file);
      console.log('🖼️ Updated image URL created');
      
      // Save current state to history before applying
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          const currentStep: HistoryStep = {
            imageData: ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height),
            hasMask: hasMask
          };
          setHistory(prev => [...prev.slice(-20), currentStep]);
          setRedoHistory([]);
        }
      }
      
      // Clear mask since image is being replaced
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }
      setHasMask(false);
      
      // Update the displayed image URL
      console.log('🔄 Updating display image URL...');
      setDisplayImageUrl(url);
      console.log('✨ Auto-straighten complete!');
      
      // Keep grid visible for 2 seconds to show result
      setTimeout(() => {
        setShowAlignmentGrid(false);
      }, 2000);
      
    } catch (error: any) {
      console.error('❌ Auto-straighten error:', error);
      setAlignmentError('Failed to straighten image. Please try again.');
      setTimeout(() => {
        setShowAlignmentGrid(false);
        setAlignmentError(null);
      }, 3000);
    } finally {
      setIsAligning(false);
    }
  };

  const creditText = (operation: string) => {
    const credits = actionCredits?.[operation] ?? quotePhotoclearOperation(operation)?.creditCost;
    if (credits == null) return '';
    return formatPhotoclearCreditLabel(credits);
  };

  const CreditBadge = ({ operation }: { operation: string }) => {
    const text = creditText(operation);
    if (!text) return null;
    return (
      <span className="ml-auto shrink-0 rounded-md border border-white/10 bg-black/40 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-orange-100">
        {text}
      </span>
    );
  };

  // Mobile action button component for cleaner code
  const BlueSkyFanControl = ({ compact = false }: { compact?: boolean }) => {
    const fanReady = useDelayedOpen(blueSkyFanOpen, 70);
    return (
      <div
        data-sky-fan
        className={`
          rounded-xl border transition-all
          ${blueSkyFanOpen ? 'overflow-visible' : 'overflow-hidden'}
          ${isProcessing
            ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed'
            : blueSkyFanOpen
            ? 'bg-slate-800 border-sky-500/60 shadow-md shadow-sky-900/20'
            : 'bg-slate-800 border-slate-700 hover:border-sky-500/50 hover:shadow-md'
          }
        `}
      >
        <button
          type="button"
          onClick={() => {
            if (isProcessing) return;
            setDeclutterFanOpen(false);
            setBrightenFanOpen(false);
            setSunsetFanOpen(false);
            setFlowerPickerOpen(false);
            setPlantPickerOpen(false);
            setBlueSkyFanOpen((open) => !open);
          }}
          disabled={isProcessing}
          className={`w-full text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors ${compact ? 'py-2.5 px-3' : 'py-3 px-4'}`}
        >
          <div className="p-1.5 bg-sky-900/30 rounded-lg text-sky-400 shrink-0">
            <Cloud size={compact ? 18 : 16} />
          </div>
          <div className="flex flex-col items-start flex-1 min-w-0">
            <span className={`font-medium ${compact ? 'text-xs text-sky-100' : 'text-sm text-sky-200'}`}>
              {isProcessing ? 'Processing...' : 'Blue Sky'}
            </span>
            <span className="text-[10px] text-sky-400/70 leading-none mt-0.5">
              {blueSkyFanOpen ? 'Choose a sky style' : 'Replace exterior sky'}
            </span>
          </div>
          <CreditBadge operation="photoclear_blue_sky" />
          <ChevronDown
            size={16}
            className={`text-sky-400/70 shrink-0 transition-transform duration-300 ${blueSkyFanOpen ? 'rotate-180' : ''}`}
          />
        </button>

        <div className={`sky-fan ${blueSkyFanOpen ? 'sky-fan-open' : ''} ${fanReady ? 'sky-fan-spread' : ''}`}>
          <div className="sky-fan-inner">
            <div className="sky-fan-stage">
              {BLUE_SKY_TYPES.map((type, index) => (
                <button
                  key={type.id}
                  type="button"
                  disabled={isProcessing || !fanReady}
                  onClick={() => {
                    handleBlueSky(type.id);
                    if (compact) setMobileSheetOpen(false);
                  }}
                  className="sky-fan-swatch"
                  style={skyFanVars(index, BLUE_SKY_TYPES.length, {
                    step: compact ? 44 : 50,
                    arch: compact ? 24 : 28,
                    maxAngle: 14,
                    delayStep: 45,
                  })}
                  title={`${type.label}, ${creditText('photoclear_blue_sky')}`}
                  aria-label={`${type.label}, ${creditText('photoclear_blue_sky')}`}
                >
                  <span className="sky-fan-disc" style={{ background: type.swatch }} />
                  <span className="sky-fan-label">{type.label}</span>
                  <span className="sky-fan-price">{creditText('photoclear_blue_sky')}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SunsetFanControl = ({ compact = false }: { compact?: boolean }) => {
    const fanReady = useDelayedOpen(sunsetFanOpen, 70);
    return (
      <div
        data-sky-fan
        className={`
          sky-fan-sunset rounded-xl border transition-all
          ${sunsetFanOpen ? 'overflow-visible -mx-4' : 'overflow-hidden'}
          ${isProcessing
            ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed'
            : sunsetFanOpen
            ? 'bg-slate-800 border-orange-500/60 shadow-md shadow-orange-900/20'
            : 'bg-slate-800 border-slate-700 hover:border-orange-500/50 hover:shadow-md'
          }
        `}
      >
        <button
          type="button"
          onClick={() => {
            if (isProcessing) return;
            setDeclutterFanOpen(false);
            setBrightenFanOpen(false);
            setBlueSkyFanOpen(false);
            setFlowerPickerOpen(false);
            setPlantPickerOpen(false);
            setSunsetFanOpen((open) => !open);
          }}
          disabled={isProcessing}
          className={`w-full text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors ${compact ? 'py-2.5 px-3' : 'py-3 px-4'}`}
        >
          <div className="p-1.5 bg-orange-900/30 rounded-lg text-orange-400 shrink-0">
            <Sunset size={compact ? 18 : 16} />
          </div>
          <div className="flex flex-col items-start flex-1 min-w-0">
            <span className={`font-medium ${compact ? 'text-xs text-orange-100' : 'text-sm text-orange-200'}`}>
              {isProcessing ? 'Processing...' : 'Sunset Sky'}
            </span>
            <span className="text-[10px] text-orange-400/70 leading-none mt-0.5">
              {sunsetFanOpen ? 'Choose a sunset style' : 'Replace with dramatic sunset'}
            </span>
          </div>
          <CreditBadge operation="photoclear_sunset_sky" />
          <ChevronDown
            size={16}
            className={`text-orange-400/70 shrink-0 transition-transform duration-300 ${sunsetFanOpen ? 'rotate-180' : ''}`}
          />
        </button>

        <div className={`sky-fan ${sunsetFanOpen ? 'sky-fan-open' : ''} ${fanReady ? 'sky-fan-spread' : ''}`}>
          <div className="sky-fan-inner">
            <div className="sky-fan-stage">
              {SUNSET_TYPES.map((type, index) => (
                <button
                  key={type.id}
                  type="button"
                  disabled={isProcessing || !fanReady}
                  onClick={() => {
                    handleSunsetSky(type.id);
                    if (compact) setMobileSheetOpen(false);
                  }}
                  className="sky-fan-swatch"
                  style={skyFanVars(index, SUNSET_TYPES.length, {
                    step: compact ? 46 : 50,
                    arch: compact ? 34 : 40,
                    maxAngle: 16,
                    delayStep: 40,
                  })}
                  title={`${type.label}, ${creditText('photoclear_sunset_sky')}`}
                  aria-label={`${type.label}, ${creditText('photoclear_sunset_sky')}`}
                >
                  <span className="sky-fan-disc" style={{ background: type.swatch }} />
                  <span className="sky-fan-label">{type.label}</span>
                  <span className="sky-fan-price">{creditText('photoclear_sunset_sky')}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const DeclutterFanControl = ({ compact = false }: { compact?: boolean }) => {
    const fanReady = useDelayedOpen(declutterFanOpen, 70);
    return (
      <div
        data-sky-fan
        className={`
          sky-fan-declutter rounded-xl border transition-all
          ${declutterFanOpen ? 'overflow-visible' : 'overflow-hidden'}
          ${isProcessing
            ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed'
            : declutterFanOpen
            ? 'bg-slate-800 border-orange-500/60 shadow-md shadow-orange-900/20'
            : 'bg-slate-800 border-slate-700 hover:border-orange-500/50 hover:shadow-md'
          }
        `}
      >
        <button
          type="button"
          onClick={() => {
            if (isProcessing) return;
            setBrightenFanOpen(false);
            setBlueSkyFanOpen(false);
            setSunsetFanOpen(false);
            setFlowerPickerOpen(false);
            setPlantPickerOpen(false);
            setDeclutterFanOpen((open) => !open);
          }}
          disabled={isProcessing}
          className={`w-full text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors ${compact ? 'py-2.5 px-3' : 'py-3 px-4'}`}
        >
          <div className="p-1.5 bg-orange-900/30 rounded-lg text-orange-400 shrink-0">
            <Sparkles size={compact ? 18 : 16} />
          </div>
          <div className="flex flex-col items-start flex-1 min-w-0">
            <span className={`font-medium ${compact ? 'text-xs text-orange-100' : 'text-sm text-orange-200'}`}>
              {isProcessing ? 'Processing...' : 'Declutter'}
            </span>
            <span className="text-[10px] text-orange-400/70 leading-none mt-0.5">
              {declutterFanOpen
                ? 'Choose Light, Medium or Full'
                : hasMask
                ? 'Applied to selection'
                : 'Clean the room automatically'}
            </span>
          </div>
          <ChevronDown
            size={16}
            className={`text-orange-400/70 shrink-0 transition-transform duration-300 ${declutterFanOpen ? 'rotate-180' : ''}`}
          />
        </button>

        <div className={`sky-fan ${declutterFanOpen ? 'sky-fan-open' : ''} ${fanReady ? 'sky-fan-spread' : ''}`}>
          <div className="sky-fan-inner">
            <div className="sky-fan-stage">
              {DECLUTTER_INTENSITIES.map((intensity, index) => (
                <button
                  key={intensity.id}
                  type="button"
                  disabled={isProcessing || !fanReady}
                  onClick={() => {
                    handleDeclutterIntensity(intensity.id);
                    if (compact) setMobileSheetOpen(false);
                  }}
                  className="sky-fan-swatch"
                  style={skyFanVars(index, DECLUTTER_INTENSITIES.length, {
                    step: compact ? 73 : 83,
                    arch: compact ? 29 : 34,
                    maxAngle: 12,
                    delayStep: 50,
                  })}
                  title={`${intensity.label}. ${intensity.description}. ${creditText(`photoclear_declutter_${intensity.id}`)}`}
                  aria-label={`${intensity.label}, ${creditText(`photoclear_declutter_${intensity.id}`)}`}
                >
                  <span className="sky-fan-disc" style={{ background: intensity.swatch }}>
                    <DeclutterIcon />
                  </span>
                  <span className="sky-fan-label">{intensity.fanLabel}</span>
                  <span className="sky-fan-price">{creditText(`photoclear_declutter_${intensity.id}`)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const BrightenFanControl = ({ compact = false }: { compact?: boolean }) => {
    const fanReady = useDelayedOpen(brightenFanOpen, 70);
    return (
      <div
        data-sky-fan
        className={`
          sky-fan-brighten rounded-xl border transition-all
          ${brightenFanOpen ? 'overflow-visible' : 'overflow-hidden'}
          ${isProcessing
            ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed'
            : brightenFanOpen
            ? 'bg-slate-800 border-yellow-500/60 shadow-md shadow-yellow-900/20'
            : 'bg-slate-800 border-slate-700 hover:border-yellow-500/50 hover:shadow-md'
          }
        `}
      >
        <button
          type="button"
          onClick={() => {
            if (isProcessing) return;
            setDeclutterFanOpen(false);
            setBlueSkyFanOpen(false);
            setSunsetFanOpen(false);
            setFlowerPickerOpen(false);
            setPlantPickerOpen(false);
            setBrightenFanOpen((open) => !open);
          }}
          disabled={isProcessing}
          className={`w-full text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors ${compact ? 'py-2.5 px-3' : 'py-3 px-4'}`}
        >
          <div className="p-1.5 bg-yellow-900/30 rounded-lg text-yellow-400 shrink-0">
            <Sun size={compact ? 18 : 16} />
          </div>
          <div className="flex flex-col items-start flex-1 min-w-0">
            <span className={`font-medium ${compact ? 'text-xs text-yellow-100' : 'text-sm text-yellow-200'}`}>
              {isProcessing ? 'Processing...' : 'Brighten & Enhance'}
            </span>
            <span className="text-[10px] text-yellow-400/70 leading-none mt-0.5">
              {brightenFanOpen ? 'Choose Light, Medium or Strong' : 'Improve lighting and colour'}
            </span>
          </div>
          <CreditBadge operation="photoclear_brighten_enhance" />
          <ChevronDown
            size={16}
            className={`text-yellow-400/70 shrink-0 transition-transform duration-300 ${brightenFanOpen ? 'rotate-180' : ''}`}
          />
        </button>

        <div className={`sky-fan ${brightenFanOpen ? 'sky-fan-open' : ''} ${fanReady ? 'sky-fan-spread' : ''}`}>
          <div className="sky-fan-inner">
            <div className="sky-fan-stage">
              {BRIGHTEN_ENHANCE_INTENSITIES.map((intensity, index) => (
                <button
                  key={intensity.id}
                  type="button"
                  disabled={isProcessing || !fanReady}
                  onClick={() => {
                    handleBrightenIntensity(intensity.id);
                    if (compact) setMobileSheetOpen(false);
                  }}
                  className="sky-fan-swatch"
                  style={skyFanVars(index, BRIGHTEN_ENHANCE_INTENSITIES.length, {
                    step: compact ? 73 : 83,
                    arch: compact ? 29 : 34,
                    maxAngle: 12,
                    delayStep: 50,
                  })}
                  title={`${intensity.label}. ${intensity.description}. ${creditText('photoclear_brighten_enhance')}`}
                  aria-label={`${intensity.label}, ${creditText('photoclear_brighten_enhance')}`}
                >
                  <span className="sky-fan-disc" style={{ background: intensity.swatch }} />
                  <span className="sky-fan-label">{intensity.fanLabel}</span>
                  <span className="sky-fan-price">{creditText('photoclear_brighten_enhance')}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const BotanicalSwatchGrid = ({
    types,
    selectedId,
    onSelect,
    accent,
    open,
  }: {
    types: { id: string; label: string; swatch: string }[];
    selectedId: string;
    onSelect: (id: string) => void;
    accent: 'pink' | 'emerald';
    open: boolean;
  }) => {
    const animate = useDelayedOpen(open, 90);
    return (
    <div className={`botanical-grid ${animate ? 'botanical-grid-open' : ''}`}>
      {types.map((type, index) => (
        <button
          key={type.id}
          type="button"
          title={type.label}
          disabled={isProcessing || !open}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(type.id);
          }}
          className={`botanical-swatch botanical-swatch-${accent} ${selectedId === type.id ? 'botanical-swatch-selected' : ''}`}
          style={{ ['--swatch-delay' as string]: `${index * 28}ms` }}
        >
          <span className="botanical-disc" style={{ background: type.swatch }}>
            <BotanicalIcon id={type.id} />
          </span>
        </button>
      ))}
    </div>
    );
  };

  const MobileActionButton = ({ onClick, icon: Icon, label, color = 'orange', operation }: { onClick: () => void, icon: any, label: string, color?: string, operation?: string }) => {
    const colorClasses: Record<string, string> = {
      orange: 'bg-orange-900/30 text-orange-400',
      green: 'bg-green-900/30 text-green-400',
      rose: 'bg-rose-900/30 text-rose-400',
      blue: 'bg-blue-900/30 text-blue-400',
      yellow: 'bg-yellow-900/30 text-yellow-400',
      pink: 'bg-pink-900/30 text-pink-400',
      emerald: 'bg-emerald-900/30 text-emerald-400',
      red: 'bg-red-900/30 text-red-400',
      slate: 'bg-slate-700 text-slate-300',
      violet: 'bg-violet-900/30 text-violet-400',
      fuchsia: 'bg-fuchsia-900/30 text-fuchsia-400',
      teal: 'bg-teal-900/30 text-teal-400',
      indigo: 'bg-indigo-900/30 text-indigo-400',
    };
    return (
      <button
        onClick={() => { onClick(); setMobileSheetOpen(false); }}
        disabled={isProcessing}
        className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-slate-800 border border-slate-700 hover:border-slate-600 active:scale-95 transition-all disabled:opacity-50"
      >
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon size={20} />
        </div>
        <span className="text-[11px] text-slate-300 font-medium text-center leading-tight">{label}</span>
        {operation && <span className="text-[9px] font-semibold text-orange-200 leading-none">{creditText(operation)}</span>}
      </button>
    );
  };

  const hasCustomPromptStagingIntent = hasBlockedStagingIntent(customPrompt);

  return (
    <div className="flex flex-col h-full bg-black">
      <style>{`
        @keyframes scan {
            0% { top: 0%; opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { top: 100%; opacity: 0; }
        }
        .animate-scan {
            animation: scan 2s linear infinite;
        }
      `}</style>
      
      {/* 360 Image - opt-in experimental declutter banner */}
      {is360Image && !enable360Editing && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-3 max-w-xl px-4">
          <div className="bg-amber-600/95 backdrop-blur-sm text-white px-6 py-4 rounded-xl shadow-xl text-sm border border-amber-400/30">
            <div className="flex items-start gap-3">
              <span className="text-3xl flex-shrink-0">🌐</span>
              <div>
                <div className="font-bold text-base mb-2">360° Image Detected</div>
                <div className="text-xs opacity-95 leading-relaxed">
                  Straighten stays off to protect the panorama projection. You can view it
                  immersively, or enable experimental declutter with brush masking — then
                  check the result in the 360° viewer before downloading.
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setShow360Viewer(true)}
              className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-3 rounded-xl shadow-lg font-semibold text-sm flex items-center gap-2 transition-all hover:scale-105 active:scale-95 border border-blue-400/30"
            >
              <Eye size={18} />
              Open 360° Viewer
            </button>
            <button
              onClick={() => setEnable360Editing(true)}
              className="bg-orange-600 hover:bg-orange-500 text-white px-5 py-3 rounded-xl shadow-lg font-semibold text-sm flex items-center gap-2 transition-all hover:scale-105 active:scale-95 border border-orange-400/30"
            >
              <Sparkles size={18} />
              Try Experimental Declutter
            </button>
          </div>
        </div>
      )}
      {is360Image && enable360Editing && (
        <div className="absolute top-[4.5rem] left-1/2 -translate-x-1/2 z-40 max-w-lg px-4 pointer-events-none">
          <div className="bg-orange-900/90 backdrop-blur-sm text-orange-100 px-4 py-2 rounded-lg shadow-lg text-xs border border-orange-500/40 text-center">
            Experimental 360° mode — brush + AI on; preview in the 360° viewer before downloading
          </div>
        </div>
      )}
      
      {/* Toolbar - Desktop */}
      <div className="hidden md:flex h-16 bg-slate-900 border-b border-slate-800 items-center justify-between px-6 shrink-0 shadow-sm z-10">
        <div className="flex items-center gap-4">
          {canUseBrush && (
            <>
              <div className="flex items-center bg-slate-800 rounded-lg p-1 gap-1">
                <button
                  onClick={() => { setMode('brush'); setIsPlacingPin(false); }}
                  className={`p-2 rounded-md transition-all ${mode === 'brush' && !isPlacingPin ? 'bg-slate-700 shadow text-orange-400' : 'text-slate-400 hover:text-slate-200'}`}
                  title="Brush Tool"
                >
                  <Brush size={20} />
                </button>
                <button
                  onClick={() => { setMode('eraser'); setIsPlacingPin(false); }}
                  className={`p-2 rounded-md transition-all ${mode === 'eraser' && !isPlacingPin ? 'bg-slate-700 shadow text-orange-400' : 'text-slate-400 hover:text-slate-200'}`}
                  title="Eraser Tool"
                >
                  <Eraser size={20} />
                </button>
              </div>

              <div className="h-6 w-px bg-slate-700 mx-2"></div>

              <div className="flex items-center gap-3">
                 <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Size</span>
                 <input 
                   type="range" 
                   min={MIN_BRUSH_SIZE} 
                   max={MAX_BRUSH_SIZE} 
                   value={brushSize} 
                   onChange={(e) => setBrushSize(parseInt(e.target.value))}
                   className="w-32 accent-orange-500 cursor-pointer"
                 />
                 <div 
                   className="rounded-full bg-orange-500/20 border border-orange-500"
                   style={{ width: brushSize / 2, height: brushSize / 2 }}
                 ></div>
              </div>
            </>
          )}
          {is360Image && !enable360Editing && (
            <div className="text-slate-400 text-sm">
              Masking & straighten disabled until you enable experimental declutter
            </div>
          )}
          {is360Image && enable360Editing && (
            <div className="text-slate-500 text-xs">
              Straighten stays off for 360°
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {canUseBrush && (
            <>
              <button
                onClick={handleUndo}
                disabled={history.length === 0}
                className="p-2 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                title="Undo"
              >
                <Undo size={20} />
              </button>
              <button
                onClick={handleRedo}
                disabled={redoHistory.length === 0}
                className="p-2 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                title="Redo"
              >
                <Redo size={20} />
              </button>
              <button
                onClick={handleClear}
                disabled={!hasMask && !pinLocation}
                className="p-2 text-slate-400 hover:text-red-400 disabled:opacity-30 transition-colors"
                title="Clear Mask"
              >
                <Trash2 size={20} />
              </button>

              <div className="w-px h-6 bg-slate-800 mx-1"></div>
            </>
          )}
          
          <button
            onClick={() => window.open(window.location.origin + '/', '_blank')}
            className="p-2 text-slate-400 hover:text-orange-400 transition-colors"
            title="Edit another image (opens in new tab)"
          >
            <ImagePlus size={20} />
          </button>
          <button
            onClick={onOpenDashboard}
            className="p-2 text-slate-400 hover:text-orange-400 transition-colors"
            title="Dashboard"
          >
            <LayoutDashboard size={20} />
          </button>
          
          <button
            onClick={onDownload}
            className="p-2 text-slate-400 hover:text-orange-400 transition-colors"
            title="Download Image"
          >
            <Download size={20} />
          </button>
          
          {!is360Image && (
            <button
              onClick={() => {
                setIsDrawingVerticalLine(!isDrawingVerticalLine);
                setVerticalLineStart(null);
                setVerticalLineEnd(null);
                setMode('brush');
                setIsPlacingPin(false);
              }}
              disabled={isAligning || isProcessing}
              className={`p-2 transition-colors ${
                isDrawingVerticalLine
                  ? 'text-cyan-400 bg-cyan-900/30 rounded-md' 
                  : isAligning 
                  ? 'text-cyan-400 animate-pulse' 
                  : 'text-slate-400 hover:text-cyan-400'
              } disabled:opacity-30`}
              title="Draw Vertical Reference Line"
            >
              <Maximize2 size={20} />
            </button>
          )}
          
          {is360Image && (
            <button
              onClick={() => setShow360Viewer(true)}
              className="p-2 text-blue-400 hover:text-blue-300 transition-colors"
              title="View in 360° Viewer"
            >
              <Eye size={20} />
            </button>
          )}
        </div>
      </div>
      
      {/* Toolbar - Mobile */}
      <div className="md:hidden h-14 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-3 shrink-0 shadow-sm z-10">
        <div className="flex items-center gap-2">
          {canUseBrush ? (
            <>
              <div className="flex items-center bg-slate-800 rounded-lg p-0.5 gap-0.5">
                <button
                  onClick={() => { setMode('brush'); setIsPlacingPin(false); }}
                  className={`p-2 rounded-md transition-all ${mode === 'brush' && !isPlacingPin ? 'bg-slate-700 shadow text-orange-400' : 'text-slate-400'}`}
                >
                  <Brush size={18} />
                </button>
                <button
                  onClick={() => { setMode('eraser'); setIsPlacingPin(false); }}
                  className={`p-2 rounded-md transition-all ${mode === 'eraser' && !isPlacingPin ? 'bg-slate-700 shadow text-orange-400' : 'text-slate-400'}`}
                >
                  <Eraser size={18} />
                </button>
              </div>
              
              <input 
                type="range" 
                min={MIN_BRUSH_SIZE} 
                max={MAX_BRUSH_SIZE} 
                value={brushSize} 
                onChange={(e) => setBrushSize(parseInt(e.target.value))}
                className="w-20 accent-orange-500"
              />
            </>
          ) : (
            <button
              onClick={() => setShow360Viewer(true)}
              className="flex items-center gap-1.5 px-2 py-1.5 text-blue-400 text-xs font-medium"
            >
              <Eye size={16} />
              360° View
            </button>
          )}
        </div>

        <div className="flex items-center gap-1">
          {canUseBrush && (
            <>
              <button onClick={handleUndo} disabled={history.length === 0} className="p-2 text-slate-400 disabled:opacity-30">
                <Undo size={18} />
              </button>
              <button onClick={handleClear} disabled={!hasMask && !pinLocation} className="p-2 text-slate-400 disabled:opacity-30">
                <Trash2 size={18} />
              </button>
            </>
          )}
          {!is360Image && (
            <button 
              onClick={() => {
                setIsDrawingVerticalLine(!isDrawingVerticalLine);
                setVerticalLineStart(null);
                setVerticalLineEnd(null);
                setMode('brush');
                setIsPlacingPin(false);
              }} 
              disabled={isAligning || isProcessing}
              className={`p-2 ${
                isDrawingVerticalLine 
                  ? 'text-cyan-400 bg-cyan-900/30 rounded-md' 
                  : isAligning 
                  ? 'text-cyan-400 animate-pulse' 
                  : 'text-slate-400'
              } disabled:opacity-30`}
            >
              <Maximize2 size={18} />
            </button>
          )}
          {is360Image && (
            <button
              onClick={() => setShow360Viewer(true)}
              className="p-2 text-blue-400"
              title="View in 360° Viewer"
            >
              <Eye size={18} />
            </button>
          )}
          <button onClick={() => window.open(window.location.origin + '/', '_blank')} className="p-2 text-slate-400" title="Edit another image">
            <ImagePlus size={18} />
          </button>
          <button onClick={onOpenDashboard} className="p-2 text-slate-400">
            <LayoutDashboard size={18} />
          </button>
          <button onClick={onDownload} className="p-2 text-slate-400">
            <Download size={18} />
          </button>
        </div>
      </div>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Canvas Area */}
        <div 
          ref={containerRef}
          className="flex-1 bg-black flex items-center justify-center p-4 md:p-8 relative overflow-hidden"
        >
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-5" 
               style={{backgroundImage: 'radial-gradient(#475569 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>

          {canvasSize.width > 0 && (
            <div 
              style={{ width: canvasSize.width, height: canvasSize.height }}
              className={`relative shadow-2xl shadow-black rounded-sm overflow-hidden bg-slate-900 select-none ring-1 ring-white/5 ${
                isDrawingVerticalLine || isPlacingPin ? 'cursor-crosshair' : 'cursor-crosshair'
              }`}
            >
              <img 
                src={displayImageUrl} 
                className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                alt="Original"
              />
              
              {/* Pin Marker */}
              {pinLocation && (
                  <div 
                    className="absolute z-30 pointer-events-none"
                    style={{ left: pinLocation.x * canvasSize.width, top: pinLocation.y * canvasSize.height }}
                  >
                      <div className="relative -top-3 -left-3 flex flex-col items-center">
                         <div className="w-6 h-6 rounded-full border-2 border-white bg-pink-500 shadow-lg flex items-center justify-center animate-in zoom-in duration-200">
                             <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                         </div>
                         <div className="w-0.5 h-4 bg-white/50 absolute top-5 shadow-[0_0_2px_black]"></div>
                      </div>
                  </div>
              )}
              
              {/* Vertical Line Markers */}
              {verticalLineStart && (
                  <div 
                    className="absolute z-30 pointer-events-none"
                    style={{ left: verticalLineStart.x, top: verticalLineStart.y }}
                  >
                      <div className="relative -top-2 -left-2 w-4 h-4 rounded-full border-2 border-cyan-400 bg-cyan-500 shadow-lg animate-pulse" />
                  </div>
              )}
              {verticalLineStart && verticalLineEnd && (
                  <>
                      <svg className="absolute inset-0 z-30 pointer-events-none" width={canvasSize.width} height={canvasSize.height}>
                          <line 
                              x1={verticalLineStart.x} 
                              y1={verticalLineStart.y} 
                              x2={verticalLineEnd.x} 
                              y2={verticalLineEnd.y} 
                              stroke="cyan" 
                              strokeWidth="3" 
                              strokeDasharray="5,5"
                          />
                      </svg>
                      <div 
                        className="absolute z-30 pointer-events-none"
                        style={{ left: verticalLineEnd.x, top: verticalLineEnd.y }}
                      >
                          <div className="relative -top-2 -left-2 w-4 h-4 rounded-full border-2 border-cyan-400 bg-cyan-500 shadow-lg animate-pulse" />
                      </div>
                  </>
              )}

              {/* Always keep the mask canvas mounted so buffer size stays in sync.
                  For locked 360 images, ignore pointer events until opt-in. */}
              <canvas
                ref={canvasRef}
                onMouseDown={canUseBrush ? handleCanvasMouseDown : undefined}
                onMouseMove={canUseBrush ? draw : undefined}
                onMouseUp={canUseBrush ? stopDrawing : undefined}
                onMouseLeave={canUseBrush ? stopDrawing : undefined}
                onTouchStart={canUseBrush ? handleCanvasMouseDown : undefined}
                onTouchMove={canUseBrush ? draw : undefined}
                onTouchEnd={canUseBrush ? stopDrawing : undefined}
                className={`absolute inset-0 z-10 touch-none ${
                  !canUseBrush ? 'pointer-events-none' : ''
                } ${
                  isDrawingVerticalLine || isPlacingPin ? 'cursor-crosshair' : ''
                }`}
              />
              
              {/* Alignment Grid Overlay */}
              <canvas
                ref={gridCanvasRef}
                className="absolute inset-0 z-20 pointer-events-none"
                style={{ display: showAlignmentGrid ? 'block' : 'none' }}
              />
              
              {/* Drawing Vertical Line Instructions */}
              {isDrawingVerticalLine && !verticalLineStart && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 bg-cyan-900/95 backdrop-blur-sm px-6 py-3 rounded-xl shadow-xl border border-cyan-700 animate-in fade-in slide-in-from-top duration-300">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold animate-pulse">1</div>
                    <p className="text-sm font-semibold text-cyan-100">Click on a point along a vertical edge (window, door, wall)</p>
                  </div>
                </div>
              )}
              
              {isDrawingVerticalLine && verticalLineStart && !verticalLineEnd && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 bg-cyan-900/95 backdrop-blur-sm px-6 py-3 rounded-xl shadow-xl border border-cyan-700 animate-in fade-in slide-in-from-top duration-300">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold animate-pulse">2</div>
                    <p className="text-sm font-semibold text-cyan-100">Click on another point along the same vertical edge</p>
                  </div>
                </div>
              )}
              
              {/* Alignment Status Message */}
              {(isAligning || alignmentError) && !isDrawingVerticalLine && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 bg-slate-900/95 backdrop-blur-sm px-6 py-3 rounded-xl shadow-xl border border-slate-700 animate-in fade-in slide-in-from-top duration-300">
                  <div className="flex items-center gap-3">
                    {isAligning ? (
                      <>
                        <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                        <div>
                          <p className="text-sm font-semibold text-cyan-200">Analyzing Image...</p>
                          <p className="text-xs text-slate-400">Detecting vertical lines</p>
                        </div>
                      </>
                    ) : alignmentError ? (
                      <>
                        <X className="w-5 h-5 text-red-400" />
                        <div>
                          <p className="text-sm font-semibold text-red-200">Alignment Notice</p>
                          <p className="text-xs text-slate-400">{alignmentError}</p>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              )}
              
              {/* Processing Overlay */}
              {isProcessing && (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/60 backdrop-blur-[2px] animate-in fade-in duration-300">
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="w-full h-1 bg-gradient-to-r from-transparent via-orange-400 to-transparent absolute top-0 animate-scan shadow-[0_0_15px_rgba(249,115,22,0.5)]"></div>
                    </div>
                    <div className="bg-slate-900/90 p-4 pr-6 rounded-2xl shadow-2xl border border-slate-700 flex items-center gap-4 backdrop-blur-md">
                        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                        <div>
                            <p className="font-semibold text-slate-100">Processing Image...</p>
                            <p className="text-xs text-slate-400">AI is cleaning up the scene</p>
                        </div>
                    </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar Controls - Desktop Only */}
        <div className={`hidden md:flex w-80 bg-slate-900 border-l border-slate-800 flex-col shrink-0 shadow-xl z-20 overflow-y-auto ${is360Image && !enable360Editing ? 'pointer-events-none opacity-30' : ''}`}>
            {/* Auto Mode Section */}
            <div className="p-6 border-b border-slate-800 bg-gradient-to-b from-orange-900/10 to-transparent">
              <h3 className="font-semibold text-orange-300 mb-4 flex items-center gap-2">
                <Sparkles size={16} className="text-orange-400 fill-orange-900/50"/>
                AI Enhancements
                {is360Image && enable360Editing && (
                  <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-orange-400/80 bg-orange-900/40 px-2 py-0.5 rounded">
                    360° experimental
                  </span>
                )}
              </h3>
              <p className="text-[11px] text-slate-400 -mt-2 mb-4 leading-snug">
                Each tool, including declutter, uses 1 credit per generated edit.
              </p>
              
              <div className="space-y-3">
                {/* Remove Selection Button - Only shown when mask is present */}
                {hasMask && (
                  <button
                    onClick={() => handleAutoProcess(DEFAULT_DECLUTTER_PROMPT, 'photoclear_remove_selection')}
                    disabled={isProcessing}
                    className={`
                      w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border-2
                      ${isProcessing
                        ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-red-900/30 to-red-800/30 border-red-600/60 text-red-200 hover:from-red-800/40 hover:to-red-700/40 hover:border-red-500 hover:shadow-lg hover:shadow-red-900/20 active:scale-[0.98]'
                      }
                    `}
                  >
                    <div className="p-1.5 bg-red-600/40 rounded-lg text-red-300 shrink-0">
                        <Eraser size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span className="font-bold">{isProcessing ? 'Processing...' : `Generate — ${creditText('photoclear_remove_selection')}`}</span>
                        <span className="text-[10px] text-red-300/70 leading-none mt-0.5">Delete items in painted area</span>
                    </div>
                  </button>
                )}

                <DeclutterFanControl />

                <button
                  onClick={() => handleAutoProcess(BED_DECLUTTER_PROMPT, 'photoclear_bed_straighten')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-rose-200 hover:bg-slate-700 hover:border-rose-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-rose-900/30 rounded-lg text-rose-400 shrink-0">
                        <BedDouble size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Straighten Bed'}</span>
                        {hasMask && <span className="text-[10px] text-rose-400/70 leading-none mt-0.5">Applied to selection</span>}
                    </div>
                    <CreditBadge operation="photoclear_bed_straighten" />
                </button>

                <button
                  onClick={() => handleAutoProcess(SOFA_STRAIGHTEN_PROMPT, 'photoclear_sofa_straighten')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-violet-200 hover:bg-slate-700 hover:border-violet-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-violet-900/30 rounded-lg text-violet-400 shrink-0">
                        <Sofa size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Straighten Sofa'}</span>
                        {hasMask && <span className="text-[10px] text-violet-400/70 leading-none mt-0.5">Applied to selection</span>}
                    </div>
                    <CreditBadge operation="photoclear_sofa_straighten" />
                </button>

                <button
                  onClick={() => handleAutoProcess(GARDEN_DECLUTTER_PROMPT, 'photoclear_garden_declutter')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-green-200 hover:bg-slate-700 hover:border-green-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-green-900/30 rounded-lg text-green-400 shrink-0">
                        <Flower2 size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Tidy Garden'}</span>
                        {hasMask && <span className="text-[10px] text-green-400/70 leading-none mt-0.5">Applied to selection</span>}
                    </div>
                    <CreditBadge operation="photoclear_garden_declutter" />
                </button>

                <button
                  onClick={() => handleAutoProcess(CUT_GRASS_PROMPT, 'photoclear_cut_grass')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-emerald-200 hover:bg-slate-700 hover:border-emerald-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-emerald-900/30 rounded-lg text-emerald-400 shrink-0">
                        <Scissors size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Cut the grass'}</span>
                        <span className="text-[10px] text-emerald-400/70 leading-none mt-0.5">
                          {hasMask ? 'Applied to selection' : 'Even, freshly mown lawn'}
                        </span>
                    </div>
                    <CreditBadge operation="photoclear_cut_grass" />
                </button>

                <button
                  onClick={() => handleAutoProcess(DRIVEWAY_DECLUTTER_PROMPT, 'photoclear_driveway_declutter')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-orange-200 hover:bg-slate-700 hover:border-orange-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-orange-900/30 rounded-lg text-orange-400 shrink-0">
                        <Trash2 size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Tidy Drive & Bins'}</span>
                        {hasMask && <span className="text-[10px] text-orange-400/70 leading-none mt-0.5">Applied to selection</span>}
                    </div>
                    <CreditBadge operation="photoclear_driveway_declutter" />
                </button>

                <button
                  onClick={() => handleAutoProcess(BINS_ONLY_PROMPT, 'photoclear_bins_only')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-slate-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-slate-700 rounded-lg text-slate-300 shrink-0">
                        <Trash2 size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Remove Bins Only'}</span>
                        {hasMask && <span className="text-[10px] text-slate-400/70 leading-none mt-0.5">Applied to selection</span>}
                    </div>
                    <CreditBadge operation="photoclear_bins_only" />
                </button>

                <button
                  onClick={() => handleAutoProcess(CARS_ONLY_PROMPT, 'photoclear_cars_only')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-blue-200 hover:bg-slate-700 hover:border-blue-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-blue-900/30 rounded-lg text-blue-400 shrink-0">
                        <Car size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Remove Cars Only'}</span>
                        {hasMask && <span className="text-[10px] text-blue-400/70 leading-none mt-0.5">Applied to selection</span>}
                    </div>
                    <CreditBadge operation="photoclear_cars_only" />
                </button>

                <BrightenFanControl />

                <button
                  onClick={() => handleAutoProcess(TURN_ON_LIGHTS_PROMPT, 'photoclear_turn_on_lights')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-amber-200 hover:bg-slate-700 hover:border-amber-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-amber-900/30 rounded-lg text-amber-400 shrink-0">
                        <Lightbulb size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Turn On Lights'}</span>
                        <span className="text-[10px] text-amber-400/70 leading-none mt-0.5">Illuminate all light fixtures</span>
                    </div>
                    <CreditBadge operation="photoclear_turn_on_lights" />
                </button>

                <button
                  onClick={() => handleAutoProcess(BLUR_PERSONAL_PHOTOS_PROMPT, 'photoclear_blur_personal_photos')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-fuchsia-200 hover:bg-slate-700 hover:border-fuchsia-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-fuchsia-900/30 rounded-lg text-fuchsia-400 shrink-0">
                        <EyeOff size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Blur personal photos'}</span>
                        <span className="text-[10px] text-fuchsia-400/70 leading-none mt-0.5">
                          {hasMask ? 'Applied to selection' : 'Frames, collages and screens'}
                        </span>
                    </div>
                    <CreditBadge operation="photoclear_blur_personal_photos" />
                </button>

                <button
                  onClick={() => handleAutoProcess(BLUR_LICENSE_PLATES_PROMPT, 'photoclear_blur_license_plates')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-teal-200 hover:bg-slate-700 hover:border-teal-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-teal-900/30 rounded-lg text-teal-400 shrink-0">
                        <RectangleEllipsis size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Blur license plates'}</span>
                        <span className="text-[10px] text-teal-400/70 leading-none mt-0.5">
                          {hasMask ? 'Applied to selection' : 'Registration numbers on vehicles'}
                        </span>
                    </div>
                    <CreditBadge operation="photoclear_blur_license_plates" />
                </button>

                <button
                  onClick={() => handleAutoProcess(REMOVE_SHADOWS_PROMPT, 'photoclear_remove_shadows')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-indigo-200 hover:bg-slate-700 hover:border-indigo-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-indigo-900/30 rounded-lg text-indigo-400 shrink-0">
                        <SunDim size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Remove shadows'}</span>
                        <span className="text-[10px] text-indigo-400/70 leading-none mt-0.5">
                          {hasMask ? 'Applied to selection' : 'Soften harsh and cast shadows'}
                        </span>
                    </div>
                    <CreditBadge operation="photoclear_remove_shadows" />
                </button>

                {/* Sky Replacement - Blue Sky */}
                <BlueSkyFanControl />

                {/* Sky Replacement - Sunset */}
                <SunsetFanControl />

                {/* Straighten Image - below Blue Sky */}
                <button
                  onClick={() => {
                    setIsDrawingVerticalLine(!isDrawingVerticalLine);
                    setVerticalLineStart(null);
                    setVerticalLineEnd(null);
                    setMode('brush');
                    setIsPlacingPin(false);
                  }}
                  disabled={isProcessing || isAligning}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border-2
                    ${isProcessing || isAligning
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed'
                      : isDrawingVerticalLine
                      ? 'bg-gradient-to-r from-cyan-800/50 to-cyan-700/50 border-cyan-500/80 text-cyan-100 shadow-lg shadow-cyan-900/30'
                      : 'bg-gradient-to-r from-cyan-900/30 to-cyan-800/30 border-cyan-600/60 text-cyan-200 hover:from-cyan-800/40 hover:to-cyan-700/40 hover:border-cyan-500 hover:shadow-lg hover:shadow-cyan-900/20 active:scale-[0.98]'
                    }
                  `}
                >
                  <div className={`p-1.5 bg-cyan-600/40 rounded-lg text-cyan-300 shrink-0 ${isAligning ? 'animate-pulse' : ''}`}>
                    <Maximize2 size={16} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold">
                      {isAligning ? 'Aligning...' : isDrawingVerticalLine ? 'Draw Vertical Line' : 'Straighten Image'}
                    </span>
                    <span className="text-[10px] text-cyan-300/70 leading-none mt-0.5">
                      {isDrawingVerticalLine ? 'Click 2 points on a vertical edge (e.g. wall corner)' : 'Fix vertical perspective — draw a line on a wall'}
                    </span>
                  </div>
                  <CreditBadge operation="photoclear_straighten_image" />
                </button>

                {/* Window Sky Replacement - Interior */}
                <button
                  onClick={() => handleAutoProcess(WINDOW_SKY_PROMPT, 'photoclear_window_sky')}
                  disabled={isProcessing}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all border
                    ${isProcessing
                      ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed' 
                      : 'bg-slate-800 border-slate-700 text-cyan-200 hover:bg-slate-700 hover:border-cyan-500/50 hover:shadow-md active:scale-[0.98]'
                    }
                  `}
                >
                    <div className="p-1.5 bg-cyan-900/30 rounded-lg text-cyan-400 shrink-0">
                        <Home size={16} />
                    </div>
                    <div className="flex flex-col">
                        <span>{isProcessing ? 'Processing...' : 'Window Sky'}</span>
                        <span className="text-[10px] text-cyan-400/70 leading-none mt-0.5">Subtle sky recovery</span>
                    </div>
                    <CreditBadge operation="photoclear_window_sky" />
                </button>

                {/* Add Flowers with Options */}
                <div
                  data-botanical-picker
                  className={`
                    rounded-xl border transition-all overflow-hidden
                    ${isProcessing 
                      ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed' 
                      : flowerPickerOpen
                      ? 'bg-slate-800 border-pink-500/60 shadow-md shadow-pink-900/20'
                      : 'bg-slate-800 border-slate-700 hover:border-pink-500/50 hover:shadow-md'
                    }
                `}>
                    <button
                      onClick={() => {
                        if (isProcessing) return;
                        if (!flowerPickerOpen) {
                          setPlantPickerOpen(false);
                          setDeclutterFanOpen(false);
                          setBrightenFanOpen(false);
                          setSunsetFanOpen(false);
                          setBlueSkyFanOpen(false);
                          setFlowerPickerOpen(true);
                          return;
                        }
                        setFlowerPickerOpen(false);
                      }}
                      disabled={isProcessing}
                      className="w-full py-3 px-4 text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors"
                    >
                        <div className="p-1.5 bg-pink-900/30 rounded-lg text-pink-400 shrink-0">
                            {pinLocation ? <MapPin size={16} /> : (hasMask ? <Crosshair size={16} /> : <Flower2 size={16} />)}
                        </div>
                        <div className="flex flex-col items-start flex-1">
                            <span className="font-medium text-sm text-pink-200">
                                {pinLocation ? 'Place Flowers Here' : 'Add Flowers'}
                            </span>
                            <span className="text-[10px] opacity-70 font-normal text-pink-200/70">
                                {flowerPickerOpen
                                  ? 'Choose a type, then tap Go'
                                  : pinLocation ? 'At set location' : (hasMask ? 'At masked spot' : 'Auto-place on table')}
                            </span>
                        </div>
                        <CreditBadge operation="photoclear_add_flowers" />
                        <ChevronDown
                          size={16}
                          className={`text-pink-400/70 shrink-0 transition-transform duration-300 ${flowerPickerOpen ? 'rotate-180' : ''}`}
                        />
                    </button>
                    
                    <div className={`botanical-panel px-4 pb-3 pt-0 bg-slate-800 border-t border-slate-700/50 ${flowerPickerOpen ? 'botanical-panel-open' : ''}`}>
                        <div className="flex items-start gap-2 mt-2">
                             <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2 mb-2">
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">Flower Type</label>
                                  <span className="text-[10px] text-pink-300 truncate">
                                    {FLOWER_TYPES.find((type) => type.id === selectedFlowerId)?.label}
                                  </span>
                                </div>
                                <BotanicalSwatchGrid
                                  types={FLOWER_TYPES}
                                  selectedId={selectedFlowerId}
                                  onSelect={setSelectedFlowerId}
                                  accent="pink"
                                  open={flowerPickerOpen}
                                />
                             </div>
                             
                             <div className="flex flex-col shrink-0 gap-2">
                                <div>
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Locate</label>
                                  <button
                                     onClick={(e) => { e.stopPropagation(); setIsPlacingPin(!isPlacingPin); }}
                                     disabled={isProcessing}
                                     className={`p-2 rounded-md border transition-all ${isPlacingPin || pinLocation ? 'bg-pink-900/30 border-pink-500/50 text-pink-400' : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                                     title="Set specific location"
                                  >
                                      <MapPin size={16} />
                                  </button>
                                </div>
                                <div>
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Go · {creditText('photoclear_add_flowers')}</label>
                                  <button
                                     onClick={(e) => { e.stopPropagation(); handleAddFlowers(); }}
                                     disabled={isProcessing}
                                     className="p-2 rounded-md border bg-pink-600 border-pink-500 text-white hover:bg-pink-500 transition-all disabled:opacity-50"
                                     title={`Add flowers, ${creditText('photoclear_add_flowers')}`}
                                     aria-label={`Add flowers, ${creditText('photoclear_add_flowers')}`}
                                  >
                                      <ArrowRight size={16} />
                                  </button>
                                </div>
                             </div>
                        </div>
                    </div>
                </div>

                {/* Add Plants with Options */}
                <div
                  data-botanical-picker
                  className={`
                    rounded-xl border transition-all overflow-hidden
                    ${isProcessing 
                      ? 'bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed' 
                      : plantPickerOpen
                      ? 'bg-slate-800 border-emerald-500/60 shadow-md shadow-emerald-900/20'
                      : 'bg-slate-800 border-slate-700 hover:border-emerald-500/50 hover:shadow-md'
                    }
                `}>
                    <button
                      onClick={() => {
                        if (isProcessing) return;
                        if (!plantPickerOpen) {
                          setFlowerPickerOpen(false);
                          setDeclutterFanOpen(false);
                          setBrightenFanOpen(false);
                          setSunsetFanOpen(false);
                          setBlueSkyFanOpen(false);
                          setPlantPickerOpen(true);
                          return;
                        }
                        setPlantPickerOpen(false);
                      }}
                      disabled={isProcessing}
                      className="w-full py-3 px-4 text-left flex items-center gap-3 hover:bg-slate-700/50 transition-colors"
                    >
                        <div className="p-1.5 bg-emerald-900/30 rounded-lg text-emerald-400 shrink-0">
                            {pinLocation ? <MapPin size={16} /> : (hasMask ? <Crosshair size={16} /> : <TreeDeciduous size={16} />)}
                        </div>
                        <div className="flex flex-col items-start flex-1">
                            <span className="font-medium text-sm text-emerald-200">
                                {pinLocation ? 'Place Plant Here' : 'Add Plant'}
                            </span>
                            <span className="text-[10px] opacity-70 font-normal text-emerald-200/70">
                                {plantPickerOpen
                                  ? 'Choose a type, then tap Go'
                                  : pinLocation ? 'At set location' : (hasMask ? 'At masked spot' : 'Auto-place in room')}
                            </span>
                        </div>
                        <CreditBadge operation="photoclear_add_plants" />
                        <ChevronDown
                          size={16}
                          className={`text-emerald-400/70 shrink-0 transition-transform duration-300 ${plantPickerOpen ? 'rotate-180' : ''}`}
                        />
                    </button>
                    
                    <div className={`botanical-panel px-4 pb-3 pt-0 bg-slate-800 border-t border-slate-700/50 ${plantPickerOpen ? 'botanical-panel-open' : ''}`}>
                        <div className="flex items-start gap-2 mt-2">
                             <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2 mb-2">
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">Plant Type</label>
                                  <span className="text-[10px] text-emerald-300 truncate">
                                    {PLANT_TYPES.find((type) => type.id === selectedPlantId)?.label}
                                  </span>
                                </div>
                                <BotanicalSwatchGrid
                                  types={PLANT_TYPES}
                                  selectedId={selectedPlantId}
                                  onSelect={setSelectedPlantId}
                                  accent="emerald"
                                  open={plantPickerOpen}
                                />
                             </div>
                             
                             <div className="flex flex-col shrink-0 gap-2">
                                <div>
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Locate</label>
                                  <button
                                     onClick={(e) => { e.stopPropagation(); setIsPlacingPin(!isPlacingPin); }}
                                     disabled={isProcessing}
                                     className={`p-2 rounded-md border transition-all ${isPlacingPin || pinLocation ? 'bg-emerald-900/30 border-emerald-500/50 text-emerald-400' : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'}`}
                                     title="Set specific location"
                                  >
                                      <MapPin size={16} />
                                  </button>
                                </div>
                                <div>
                                  <label className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mb-1 block">Go · {creditText('photoclear_add_plants')}</label>
                                  <button
                                     onClick={(e) => { e.stopPropagation(); handleAddPlants(); }}
                                     disabled={isProcessing}
                                     className="p-2 rounded-md border bg-emerald-600 border-emerald-500 text-white hover:bg-emerald-500 transition-all disabled:opacity-50"
                                     title={`Add plant, ${creditText('photoclear_add_plants')}`}
                                     aria-label={`Add plant, ${creditText('photoclear_add_plants')}`}
                                  >
                                      <ArrowRight size={16} />
                                  </button>
                                </div>
                             </div>
                        </div>
                    </div>
                </div>

              </div>
            </div>

            {/* Custom Request Section (Renamed from Manual Control) */}
            <div className="p-6 flex-1 flex flex-col bg-gradient-to-t from-slate-900 to-slate-900/50">
              <h3 className="font-semibold text-slate-200 mb-2 text-sm uppercase tracking-wider flex items-center gap-2">
                 <Wand2 size={16} className="text-orange-400" />
                 Custom Request
              </h3>
              <p className="text-sm text-slate-300 mb-1">
                Photoclear is for removing items from photos.
              </p>
              <p className="text-sm text-slate-400 mb-3">
                Paint over items to modify them, or leave blank to edit the entire image.
              </p>
              <a
                href="https://staging.rehub.studio"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-amber-400 hover:text-amber-300 mb-4 px-3 py-2 rounded-lg bg-amber-950/30 border border-amber-800/50 w-fit"
              >
                <Sofa size={18} />
                Virtual Staging
              </a>
              
              <div className="space-y-2 mb-6 flex-1 flex flex-col min-h-0">
                 {hasCustomPromptStagingIntent && (
                  <p className="text-sm text-amber-300 bg-amber-900/40 border border-amber-600/60 rounded-lg px-3 py-2.5 shrink-0">
                    <span className="font-medium">Furniture and full staging</span> are handled in Virtual Staging. Use the button above or &quot;Send to Virtual Staging&quot; from your gallery.
                  </p>
                 )}
                 <textarea 
                  className="w-full flex-1 min-h-[80px] text-sm p-3 border border-slate-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none bg-slate-800 text-slate-200 placeholder-slate-500"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Describe your request (e.g. 'remove the sofa', 'make the sky blue')..."
                 />
              </div>

              <div className="mt-auto">
                <button
                  onClick={handleManualProcess}
                  disabled={isProcessing || (!hasMask && !customPrompt.trim()) || hasCustomPromptStagingIntent}
                  className={`
                    w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all shadow-lg
                    ${isProcessing || (!hasMask && !customPrompt.trim()) || hasCustomPromptStagingIntent
                      ? 'bg-slate-700 cursor-not-allowed shadow-none text-slate-500' 
                      : 'bg-orange-600 hover:bg-orange-500 hover:shadow-orange-500/25 active:scale-[0.98]'
                    }
                  `}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {hasMask && !customPrompt.trim() ? (
                        <>Remove Selected — {creditText('photoclear_remove_selection')} <ArrowRight size={20} /></>
                      ) : (
                        <>Apply Custom Edit — {creditText('photoclear_custom')} <Wand2 size={18} /></>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
        </div>
        
        {/* Mobile Bottom Sheet */}
        <div className="md:hidden">
          {/* Collapsed Bar */}
          {!mobileSheetOpen && (
            <div className="fixed bottom-0 left-0 right-0 z-30">
              <button
                onClick={() => {
                  if (is360Image && !enable360Editing) {
                    if (!require360OptIn()) return;
                  }
                  setMobileSheetOpen(true);
                }}
                className="w-full bg-gradient-to-t from-slate-900 via-slate-900 to-slate-900/95 border-t border-slate-700 p-4 flex items-center justify-center gap-2 text-orange-400 font-medium"
              >
                <Sparkles size={18} />
                <span>{is360Image && !enable360Editing ? 'Try Experimental 360° Declutter' : 'AI Enhancements'}</span>
                <ChevronUp size={18} />
              </button>
            </div>
          )}
          
          {/* Expanded Sheet */}
          {mobileSheetOpen && (
            <div className="fixed inset-0 z-40 flex flex-col">
              {/* Backdrop */}
              <div 
                className="flex-1 bg-black/60 backdrop-blur-sm"
                onClick={() => setMobileSheetOpen(false)}
              />
              
              {/* Sheet Content */}
              <div className="bg-slate-900 border-t border-slate-700 rounded-t-3xl max-h-[75vh] flex flex-col animate-in slide-in-from-bottom duration-300">
                {/* Handle */}
                <div className="flex justify-center pt-3 pb-2">
                  <div className="w-10 h-1 bg-slate-600 rounded-full" />
                </div>
                
                {/* Header */}
                <div className="flex items-center justify-between px-4 pb-3 border-b border-slate-800">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setMobileTab('quick')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${mobileTab === 'quick' ? 'bg-orange-600 text-white' : 'text-slate-400'}`}
                    >
                      Quick Actions
                    </button>
                    <button
                      onClick={() => setMobileTab('custom')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${mobileTab === 'custom' ? 'bg-orange-600 text-white' : 'text-slate-400'}`}
                    >
                      Custom
                    </button>
                  </div>
                  <button
                    onClick={() => setMobileSheetOpen(false)}
                    className="p-2 text-slate-400"
                  >
                    <X size={20} />
                  </button>
                </div>

                {is360Image && enable360Editing && (
                  <div className="px-4 py-2 bg-orange-900/40 border-b border-orange-800/50 text-[11px] text-orange-200 text-center">
                    Experimental 360° mode — preview in the 360° viewer before downloading
                  </div>
                )}
                <p className="px-4 py-2 text-[11px] text-slate-400 text-center border-b border-slate-800">
                  Each tool, including declutter, uses 1 credit per generated edit.
                </p>
                
                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  {mobileTab === 'quick' ? (
                    <>
                      {/* Remove Selection - show only when mask exists */}
                      {hasMask && (
                        <div className="mb-4">
                          <button
                            onClick={() => { handleAutoProcess(DEFAULT_DECLUTTER_PROMPT, 'photoclear_remove_selection'); setMobileSheetOpen(false); }}
                            disabled={isProcessing}
                            className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-red-900/40 to-red-800/40 border-2 border-red-600/60 text-red-200 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <Eraser size={18} />
                            Remove Selection
                          </button>
                        </div>
                      )}
                      
                      {/* Quick Action Grid */}
                      <div className="grid grid-cols-4 gap-2 mb-4">
                        <MobileActionButton onClick={() => handleAutoProcess(BED_DECLUTTER_PROMPT, 'photoclear_bed_straighten')} icon={BedDouble} label="Bed" color="rose" operation="photoclear_bed_straighten" />
                        <MobileActionButton onClick={() => handleAutoProcess(SOFA_STRAIGHTEN_PROMPT, 'photoclear_sofa_straighten')} icon={Sofa} label="Sofa" color="violet" operation="photoclear_sofa_straighten" />
                        <MobileActionButton onClick={() => handleAutoProcess(GARDEN_DECLUTTER_PROMPT, 'photoclear_garden_declutter')} icon={Flower2} label="Garden" color="green" operation="photoclear_garden_declutter" />
                        <MobileActionButton onClick={() => handleAutoProcess(CUT_GRASS_PROMPT, 'photoclear_cut_grass')} icon={Scissors} label="Cut grass" color="emerald" operation="photoclear_cut_grass" />
                        <MobileActionButton onClick={() => handleAutoProcess(DRIVEWAY_DECLUTTER_PROMPT, 'photoclear_driveway_declutter')} icon={Trash2} label="Drive" color="orange" operation="photoclear_driveway_declutter" />
                        <MobileActionButton onClick={() => handleAutoProcess(BINS_ONLY_PROMPT, 'photoclear_bins_only')} icon={Trash2} label="Bins" color="slate" operation="photoclear_bins_only" />
                        <MobileActionButton onClick={() => handleAutoProcess(CARS_ONLY_PROMPT, 'photoclear_cars_only')} icon={Car} label="Cars" color="blue" operation="photoclear_cars_only" />
                        <MobileActionButton onClick={() => handleAutoProcess(TURN_ON_LIGHTS_PROMPT, 'photoclear_turn_on_lights')} icon={Lightbulb} label="Lights On" color="yellow" operation="photoclear_turn_on_lights" />
                        <MobileActionButton onClick={() => handleAutoProcess(BLUR_PERSONAL_PHOTOS_PROMPT, 'photoclear_blur_personal_photos')} icon={EyeOff} label="Blur photos" color="fuchsia" operation="photoclear_blur_personal_photos" />
                        <MobileActionButton onClick={() => handleAutoProcess(BLUR_LICENSE_PLATES_PROMPT, 'photoclear_blur_license_plates')} icon={RectangleEllipsis} label="Blur plates" color="teal" operation="photoclear_blur_license_plates" />
                        <MobileActionButton onClick={() => handleAutoProcess(REMOVE_SHADOWS_PROMPT, 'photoclear_remove_shadows')} icon={SunDim} label="Shadows" color="indigo" operation="photoclear_remove_shadows" />
                        <MobileActionButton
                          onClick={() => {
                            setIsDrawingVerticalLine(!isDrawingVerticalLine);
                            setVerticalLineStart(null);
                            setVerticalLineEnd(null);
                            setMode('brush');
                            setIsPlacingPin(false);
                            setMobileSheetOpen(false);
                          }}
                          icon={Maximize2}
                          label="Straighten"
                          color="blue"
                          operation="photoclear_straighten_image"
                        />
                        <MobileActionButton onClick={() => handleAutoProcess(WINDOW_SKY_PROMPT, 'photoclear_window_sky')} icon={Home} label="Window" color="blue" operation="photoclear_window_sky" />
                        <MobileActionButton onClick={handleAddFlowers} icon={Flower2} label="Flowers" color="pink" operation="photoclear_add_flowers" />
                      </div>
                      <div className="mb-4 space-y-3">
                        <DeclutterFanControl compact />
                        <BrightenFanControl compact />
                        <BlueSkyFanControl compact />
                        <SunsetFanControl compact />
                      </div>
                      
                      {/* Flowers/Plants Options */}
                      <div className="space-y-3">
                        <div className="bg-slate-800 rounded-xl p-3 border border-slate-700">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <Flower2 size={16} className="text-pink-400 shrink-0" />
                              <span className="text-sm text-slate-300 font-medium">Flower Type</span>
                            </div>
                            <button
                              onClick={() => { handleAddFlowers(); setMobileSheetOpen(false); }}
                              disabled={isProcessing}
                              className="px-3 py-1.5 bg-pink-600 text-white text-xs font-medium rounded-lg disabled:opacity-50 shrink-0"
                            >
                              Add Flowers · {creditText('photoclear_add_flowers')}
                            </button>
                          </div>
                          <p className="text-[11px] text-pink-300 mb-2 truncate">
                            {FLOWER_TYPES.find((type) => type.id === selectedFlowerId)?.label}
                          </p>
                          <BotanicalSwatchGrid
                            types={FLOWER_TYPES}
                            selectedId={selectedFlowerId}
                            onSelect={setSelectedFlowerId}
                            accent="pink"
                            open={mobileSheetOpen}
                          />
                        </div>
                        
                        <div className="bg-slate-800 rounded-xl p-3 border border-slate-700">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <TreeDeciduous size={16} className="text-emerald-400 shrink-0" />
                              <span className="text-sm text-slate-300 font-medium">Plant Type</span>
                            </div>
                            <button
                              onClick={() => { handleAddPlants(); setMobileSheetOpen(false); }}
                              disabled={isProcessing}
                              className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg disabled:opacity-50 shrink-0"
                            >
                              Add Plant · {creditText('photoclear_add_plants')}
                            </button>
                          </div>
                          <p className="text-[11px] text-emerald-300 mb-2 truncate">
                            {PLANT_TYPES.find((type) => type.id === selectedPlantId)?.label}
                          </p>
                          <BotanicalSwatchGrid
                            types={PLANT_TYPES}
                            selectedId={selectedPlantId}
                            onSelect={setSelectedPlantId}
                            accent="emerald"
                            open={mobileSheetOpen}
                          />
                        </div>
                        
                        {/* Pin Location */}
                        <button
                          onClick={() => { setIsPlacingPin(true); setMobileSheetOpen(false); }}
                          className={`w-full py-3 px-4 rounded-xl border flex items-center justify-center gap-2 transition-all ${
                            pinLocation 
                              ? 'bg-pink-900/30 border-pink-500/50 text-pink-300' 
                              : 'bg-slate-800 border-slate-700 text-slate-300'
                          }`}
                        >
                          <MapPin size={18} />
                          <span>{pinLocation ? 'Location Set - Tap to Change' : 'Set Placement Location'}</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    /* Custom Tab */
                    <div className="space-y-4">
                      <p className="text-sm text-slate-300">
                        Photoclear is for removing items from photos.
                      </p>
                      <p className="text-sm text-slate-400">
                        Paint over items to modify them, or describe what you want to change.
                      </p>
                      <a
                        href="https://staging.rehub.studio"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-amber-400 hover:text-amber-300 px-3 py-2 rounded-lg bg-amber-950/30 border border-amber-800/50 w-full justify-center"
                      >
                        <Sofa size={18} />
                        Virtual Staging
                      </a>
                      {hasCustomPromptStagingIntent && (
                        <p className="text-sm text-amber-300 bg-amber-900/40 border border-amber-600/60 rounded-lg px-3 py-2.5">
                          <span className="font-medium">Furniture and full staging</span> are handled in Virtual Staging. Use the button above or &quot;Send to Virtual Staging&quot; from your gallery.
                        </p>
                      )}
                      <textarea 
                        className="w-full h-32 text-sm p-3 border border-slate-700 rounded-xl bg-slate-800 text-slate-200 placeholder-slate-500 resize-none"
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="Describe your request (e.g. 'remove the sofa', 'make the sky blue')..."
                      />
                      <button
                        onClick={() => { handleManualProcess(); setMobileSheetOpen(false); }}
                        disabled={isProcessing || (!hasMask && !customPrompt.trim()) || hasCustomPromptStagingIntent}
                        className={`
                          w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all
                          ${isProcessing || (!hasMask && !customPrompt.trim()) || hasCustomPromptStagingIntent
                            ? 'bg-slate-700 text-slate-500' 
                            : 'bg-orange-600 active:scale-[0.98]'
                          }
                        `}
                      >
                        {hasMask && !customPrompt.trim() ? (
                          <>Remove Selected <ArrowRight size={20} /></>
                        ) : (
                          <>Apply Edit <Wand2 size={18} /></>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* 360 Viewer Modal */}
      {show360Viewer && (
        <Viewer360 
          imageUrl={imageUrl} 
          onClose={() => setShow360Viewer(false)}
          title="360° Preview"
        />
      )}
    </div>
  );
};

export default Editor;
